// Performance V2 — Keywords tab (#4295). Account-wide keyword performance
// with Quality Score, IS metrics, and CSV export.
// Extracted from performance-v2.tsx (#4932 Phase 0.3); no behavior change.
// The keywords query lives HERE (it was gated on `tab === 'keywords'` in the
// shell; the module only mounts when the tab is active, so mounting preserves
// the lazy fetch and its Google Ads quota rationale). Column visibility stays
// shell-owned: the header toolbar's ColumnsPopover and the userSettings
// persistence live there.
import { Spinner } from '@/components/glitch-ui/spinner';
import type React from 'react';
import { useMemo } from 'react';
import { BarChart3 } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';

import type { KeywordOverviewData } from '@/lib/apis';
import type { CampaignData } from '@/lib/types';
import { useKeywordsOverviewReportQuery } from '@/lib/query-options';
import { useNeverLiveAds } from '@/hooks/usePerformanceData';
import { DataTable } from '@/components/glitch-ui/data-table';
import { QualityScorePill } from '@/components/glitch-ui/quality-score-pill';
import { WidgetGrid } from '@/components/glitch-ui/widget-grid';
import type { WidgetConfig } from '@/components/glitch-ui/widget-grid';
import {
  fmt, MatchTypeBadge, KeywordStatusBadge, PerformanceCell,
  EmptyTabState, parseCampaignName, MutedDash,
} from '../cells';

/** Report row plus the never-live seeding flag (ruling 09-09): a draft
    campaign's local keywords list with dashes, never zeros. */
type KeywordRow = KeywordOverviewData & { unmeasured?: boolean };

// Column-picker metadata for the Keywords table. 'performance' is the
// consolidated Imp · Clicks · Conv · Cost cell — on by default; the granular
// columns it consolidates default off so the table renders compact out of
// the box. Exported for the shell's header toolbar (ColumnsPopover) and the
// userSettings column-visibility restore.
export const KEYWORDS_TABLE_COLUMNS: { id: string; label: string; pinned?: boolean; defaultHidden?: boolean }[] = [
  { id: 'keyword',       label: 'Keyword',         pinned: true },
  { id: 'quality_score', label: 'QS' },
  { id: 'final_url',     label: 'Final URL' },
  { id: 'performance',   label: 'Performance' },
  { id: 'impressions',   label: 'Impressions',     defaultHidden: true },
  { id: 'clicks',        label: 'Clicks',          defaultHidden: true },
  { id: 'conversions',   label: 'Conversions',     defaultHidden: true },
  { id: 'cost',          label: 'Cost',            defaultHidden: true },
  { id: 'ctr',           label: 'CTR' },
  { id: 'avg_cpc',       label: 'Avg. CPC' },
  { id: 'conv_rate',     label: 'Conv. rate' },
  { id: 'cost_per_conv', label: 'Cost/conv' },
];
export const KEYWORDS_COLUMN_IDS = new Set(KEYWORDS_TABLE_COLUMNS.map(c => c.id));
export const KEYWORDS_PINNED_COLUMN_IDS = new Set(KEYWORDS_TABLE_COLUMNS.filter(c => c.pinned).map(c => c.id));
export const KEYWORDS_DEFAULT_VISIBLE_IDS = new Set(KEYWORDS_TABLE_COLUMNS.filter(c => !c.defaultHidden).map(c => c.id));

export function KeywordsTab({
  campaignIds,
  visibleColumns,
  currencySymbol,
  dateRangeFilter,
  layout,
  onReorder,
  onResize,
  renderChart,
  activeCampaigns,
}: {
  campaignIds: string | undefined;
  visibleColumns: Set<string>;
  currencySymbol: string;
  dateRangeLabel: string;
  dateRangeFilter: { start_date: string; end_date: string };
  layout: WidgetConfig[];
  onReorder: (layout: WidgetConfig[]) => void;
  onResize: (id: string, width: 33 | 50 | 66 | 100) => void;
  renderChart: () => React.ReactNode;
  /** Enables the never-live seeding (ruling 09-09): draft campaigns' local
      keywords list with dashes before anything has served. */
  activeCampaigns?: CampaignData[];
}) {
  const cs = currencySymbol;

  // #4295 — account-wide keyword performance (with Quality Score). Only
  // fetched when the user is actually viewing the Keywords tab (this module
  // mounts on tab activation); that's a sub-1k row table per account and the
  // GAds quota cost is real.
  const { data: keywordsData, isLoading: isKeywordsLoading, isError: isKeywordsError } = useKeywordsOverviewReportQuery(
    dateRangeFilter.start_date,
    dateRangeFilter.end_date,
    campaignIds || undefined,
    { enabled: !!campaignIds },
  );

  // Never-live campaigns' local keywords seed dashed rows (ruling 09-09).
  // Ads come from the per-campaign detail fan-out (#4760 light list).
  const neverLiveCampaigns = useMemo(
    () => (activeCampaigns ?? []).filter(
      (c) => !c.googleads_campaign?.googleads_campaign_id || c.state === 'draft',
    ),
    [activeCampaigns],
  );
  const neverLiveAds = useNeverLiveAds(activeCampaigns ?? []);
  const keywordRows = useMemo<KeywordRow[]>(() => {
    const rows: KeywordRow[] = [...(keywordsData ?? [])];
    const seen = new Set(rows.map((r) => `${r.keyword}::${r.match_type ?? ''}`.toLowerCase()));
    for (const c of neverLiveCampaigns) {
      const ads = c.ads?.length ? c.ads : (neverLiveAds.get(c.id) ?? []);
      ads.forEach((ad, adIndex) => {
        for (const kw of ad.keywords ?? []) {
          if (!kw.text) continue;
          const key = `${kw.text}::${kw.match_type ?? ''}`.toLowerCase();
          if (seen.has(key)) continue;
          seen.add(key);
          rows.push({
            campaign_id: `glitch-${c.id}`,
            campaign_name: parseCampaignName(c.name),
            ad_group_id: `glitch-ad-${ad.id}`,
            // The human name, not the pre-generated Google Ads ad-group name
            // (#4407's "Gl-<url>-<uuid>" is an internal identifier).
            ad_group_name: ad.name ?? `Ad ${adIndex + 1}`,
            criterion_id: `glitch-kw-${kw.id ?? key}`,
            keyword: kw.text,
            match_type: kw.match_type ?? null,
            // Draft keywords never reached Google, so our stored override is the only value there is.
            final_url: kw.final_url ?? '',
            status: kw.is_paused ? 'PAUSED' : 'ENABLED',
            quality_score: null,
            impressions: 0, clicks: 0, cost_micros: 0, cost: 0, conversions: 0,
            conv_rate: 0, cost_per_conv: 0, ctr: 0, avg_cpc: 0,
            unmeasured: true,
          });
        }
      });
    }
    return rows;
  }, [keywordsData, neverLiveCampaigns, neverLiveAds]);

  // #4295 — Keywords table columns. One row per (ad_group_id, criterion_id).
  // The "Keyword" cell carries match-type + status indicators so the table
  // doesn't need separate columns for each (saves horizontal real estate).
  // QualityScorePill renders the point-in-time QS merged backend-side.
  const keywordColumns: ColumnDef<KeywordRow, unknown>[] = [
    { accessorKey: 'keyword', header: 'Keyword', cell: ({ row }) => (
      <div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900">{row.original.keyword}</span>
          <MatchTypeBadge matchType={row.original.match_type} />
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <KeywordStatusBadge status={row.original.status} />
          <span className="text-xs text-gray-500">{row.original.campaign_name}</span>
          <span className="text-xs text-gray-400">·</span>
          <span className="text-xs text-gray-500 truncate max-w-[180px]">{row.original.ad_group_name}</span>
        </div>
      </div>
    )},
    { accessorKey: 'quality_score', header: 'QS', cell: ({ row }) => (
      <QualityScorePill
        score={row.original.quality_score}
        subScores={{
          expected_ctr: row.original.search_predicted_ctr,
          ad_relevance: row.original.creative_quality_score,
          landing_page_experience: row.original.post_click_quality_score,
        }}
      />
    ) },
    // #6582 — Final URL (keyword landing page), mirrors the Google Ads Keywords column.
    { accessorKey: 'final_url', header: 'Final URL', cell: ({ row }) => {
      const url = row.original.final_url;
      if (!url) return <span className="text-xs text-gray-400">—</span>;
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          title={url}
          className="block max-w-[220px] truncate text-sm text-[#290BD9] hover:underline"
        >
          {url.replace(/^https?:\/\//, '')}
        </a>
      );
    }},
    // Consolidated Imp · Clicks · Conv · Cost cell, plus the four granular
    // metric columns it consolidates. The granular ones default-off via the
    // column picker (KEYWORDS_TABLE_COLUMNS.defaultHidden) — users opt in
    // when they want to sort by a single metric.
    // Never-live rows dash their metric cells (ruling 09-09): nothing measured.
    { id: 'performance', header: 'Performance', enableSorting: false, cell: ({ row }) => (
      row.original.unmeasured ? <MutedDash /> : (
        <PerformanceCell
          impressions={row.original.impressions}
          clicks={row.original.clicks}
          conversions={row.original.conversions}
          cost={row.original.cost}
          currency={cs}
        />
      )
    )},
    { accessorKey: 'impressions', header: 'Imp.', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : row.original.impressions.toLocaleString() },
    { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : row.original.clicks.toLocaleString() },
    { accessorKey: 'conversions', header: 'Conv.', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : row.original.conversions.toFixed(0) },
    { accessorKey: 'cost', header: 'Cost', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : fmt(row.original.cost, cs) },
    { accessorKey: 'ctr', header: 'CTR', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : `${(row.original.ctr * 100).toFixed(2)}%` },
    { accessorKey: 'avg_cpc', header: 'Avg. CPC', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : fmt(row.original.avg_cpc, cs) },
    { accessorKey: 'conv_rate', header: 'Conv. rate', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : `${(row.original.conv_rate * 100).toFixed(1)}%` },
    { accessorKey: 'cost_per_conv', header: 'Cost/conv', cell: ({ row }) => row.original.conversions > 0 ? fmt(row.original.cost_per_conv, cs) : <MutedDash /> },
  ];
  // Filter keyword columns by user-chosen visibility — column picker writes
  // through to user settings; defaults exclude the granular metrics in
  // favour of Performance + derived columns.
  const visibleKeywordColumnDefs = keywordColumns.filter(c => {
    const id = c.id ?? ((c as { accessorKey?: string }).accessorKey);
    return id ? visibleColumns.has(id) : true;
  });

  return (
    <WidgetGrid layout={layout} onReorder={onReorder} onResize={onResize}>
      {(widgetId, _widgetWidth) => {
        switch (widgetId) {
          case 'performance-chart':
            return renderChart();

          case 'keywords-content': {
            const hasKeywords = keywordRows.length > 0;
            if (isKeywordsLoading && !hasKeywords) {
              return (
                <div className="flex items-center justify-center py-16">
                  <Spinner size="lg" className="text-gray-400" />
                  <span className="ml-3 text-sm text-gray-500">Loading keyword performance…</span>
                </div>
              );
            }
            if (isKeywordsError) {
              // #6562 — a failing report used to fall through to "No Keyword Data",
              // hiding real backend errors from users (only Sentry saw them).
              return (
                <EmptyTabState
                  icon={BarChart3}
                  title="Couldn't load keywords"
                  description="Something went wrong fetching keyword performance. Refresh to try again; if it persists, contact support."
                />
              );
            }
            if (keywordRows.length === 0) {
              return (
                <EmptyTabState
                  icon={BarChart3}
                  title="No Keyword Data"
                  description="Keyword performance will appear here once campaigns have traffic in the selected period."
                />
              );
            }
            // #4302 — CSV export for Keywords. Matches the Google Ads UI
            // Search keyword report column set (including QS + IS metrics).
            return (
              <div className="space-y-2">
                <DataTable<KeywordRow>
                  data={keywordRows}
                  columns={visibleKeywordColumnDefs}
                  pageSize={25}
                  variant="bordered"
                  emptyState="No keyword performance data available for this period"
                />
                {/* #6281 — the report is capped server-side; a full page silently
                    read as "everything" when it was a truncation. */}
                {(keywordsData?.length ?? 0) >= 1000 && (
                  <p className="text-xs text-gray-500">
                    Showing the first 1,000 keywords by impressions. Narrow the date range or campaign filter to see the rest.
                  </p>
                )}
              </div>
            );
          }

          default:
            return null;
        }
      }}
    </WidgetGrid>
  );
}
